import {
  CAMERA_BACK,
  CAMERA_DEPTH_FOV,
  CAMERA_HEIGHT,
  DRAW_DISTANCE,
  LANE_X,
  ROAD_HALF_WIDTH,
  RUMBLE_LENGTH,
  SEGMENT_LENGTH,
} from '../../shared/constants';
import type { Theme } from '../../shared/themes';
import type { Segment, Track } from '../../shared/track';
import { drawEntity, drawProp } from './sprites';
import { clamp01, mixHex, rgba, shade } from './palette';

export const CAMERA_DEPTH = 1 / Math.tan(((CAMERA_DEPTH_FOV / 2) * Math.PI) / 180);

export interface ProjectedSegment {
  seg: Segment;
  n: number;
  /** Near edge. */
  x1: number;
  y1: number;
  w1: number;
  /** Far edge. */
  x2: number;
  y2: number;
  w2: number;
  scale1: number;
  fog: number;
  drawn: boolean;
}

export interface WorldFrame {
  horizon: number;
  /** Accumulated curve offset, in pixels, for background parallax. */
  sway: number;
  /** Screen position and scale of the rider. */
  riderX: number;
  riderY: number;
  /** Projected half-road-width in pixels at the rider's depth. */
  riderRoadPx: number;
  /** Pixels per world unit at the rider's depth. */
  riderUnitPx: number;
}

export interface WorldRenderOptions {
  track: Track;
  theme: Theme;
  /** Player's world z. The camera sits CAMERA_BACK behind this. */
  playerZ: number;
  /** Player's normalised road x. */
  playerX: number;
  /** Smoothed camera x in normalised road units; lags the player. */
  cameraX: number;
  /** Player's height above the road. */
  playerY: number;
  width: number;
  height: number;
  timeS: number;
  /**
   * Screen-space camera pitch in pixels, positive when descending. Raising the
   * horizon as the trail drops away is what makes a fall line read as a fall
   * line rather than a flat road.
   */
  pitch: number;
  /** Extra riders (ghost, rival) drawn in the world at their own z. */
  others?: { z: number; x: number; y: number; draw: (ctx: CanvasRenderingContext2D, sx: number, sy: number, unitPx: number) => void }[];
}

/**
 * Segment-projected road renderer. Draws far-to-near with a running clip so a
 * crest never gets painted over by the ground behind it, then paints sprites in
 * the same order so they layer correctly.
 */
export class WorldRenderer {
  private frame: ProjectedSegment[] = [];

  render(ctx: CanvasRenderingContext2D, o: WorldRenderOptions): WorldFrame {
    const { track, theme, width: w, height: h } = o;
    const camZ = o.playerZ - CAMERA_BACK;
    const camY = track.elevationAt(Math.max(0, camZ)) + CAMERA_HEIGHT + o.playerY * 0.35;
    const camXWorld = o.cameraX * ROAD_HALF_WIDTH;
    const pitch = o.pitch;
    const horizon = h / 2 - pitch;

    const baseIndex = Math.max(0, Math.floor(camZ / SEGMENT_LENGTH));
    const basePercent = (camZ - baseIndex * SEGMENT_LENGTH) / SEGMENT_LENGTH;
    const baseSeg = track.segment(baseIndex);
    const riderIndex = Math.floor(o.playerZ / SEGMENT_LENGTH);

    let x = 0;
    let dx = -(baseSeg.curve * basePercent);
    let maxY = h;
    let sway = 0;
    let riderRec: ProjectedSegment | null = null;

    // Paint the ground everywhere below the horizon first. Segment polygons
    // cannot cover the strip between the camera and the first segment, and a
    // crest can leave a sliver, so this guarantees there is never a hole.
    ctx.fillStyle = mixHex(theme.ground.dark, theme.fog, 0.72);
    ctx.fillRect(0, Math.floor(horizon), w, h - Math.floor(horizon) + 1);

    this.frame.length = 0;

    for (let n = 0; n < DRAW_DISTANCE; n++) {
      const seg = track.segment(baseIndex + n);
      const p1 = project(seg.z, seg.y, camXWorld - x, camY, camZ, CAMERA_DEPTH, w, h, pitch);
      const p2 = project(
        seg.z + SEGMENT_LENGTH,
        seg.yEnd,
        camXWorld - x - dx,
        camY,
        camZ,
        CAMERA_DEPTH,
        w,
        h,
        pitch,
      );
      x += dx;
      dx += seg.curve;
      if (n === 24) sway = x * 0.02;

      const fog = 1 - Math.exp((-theme.fogDensity * (n / DRAW_DISTANCE) * n) / DRAW_DISTANCE);
      const rec: ProjectedSegment = {
        seg,
        n,
        x1: p1.x,
        y1: p1.y,
        w1: p1.w,
        x2: p2.x,
        y2: p2.y,
        w2: p2.w,
        scale1: p1.scale,
        fog: clamp01(fog),
        drawn: false,
      };
      this.frame.push(rec);
      if (seg.index === riderIndex) riderRec = rec;

      // Behind the camera, or hidden behind a nearer crest.
      if (p1.z <= CAMERA_DEPTH || p2.y >= p1.y || p2.y >= maxY) continue;
      rec.drawn = true;
      maxY = p2.y;
      this.drawSegment(ctx, rec, theme, w);
    }

    // Sprites, far to near, so nearer things paint over farther things.
    const riderInfo = placeRider(o, riderRec, w, h);
    for (let i = this.frame.length - 1; i >= 0; i--) {
      const rec = this.frame[i]!;
      if (!rec.drawn || rec.w1 <= 0) continue;
      // Anything between the camera and the rider has already been ridden past,
      // and at that depth perspective makes it fill the screen.
      if (rec.seg.z + SEGMENT_LENGTH < o.playerZ) continue;
      const unitPx = rec.w1 / ROAD_HALF_WIDTH;
      const alpha = 1 - rec.fog;
      if (alpha <= 0.02) continue;
      ctx.globalAlpha = alpha;
      for (const p of rec.seg.props) {
        const sx = rec.x1 + p.x * rec.w1;
        drawProp(ctx, p, sx, shoulderY(rec, sx, w), unitPx, theme, o.timeS);
      }
      for (const e of rec.seg.entities) {
        drawEntity(ctx, e, rec, unitPx, theme, o.timeS);
      }
      if (o.others) {
        for (const other of o.others) {
          const oz = other.z;
          if (oz < rec.seg.z || oz >= rec.seg.z + SEGMENT_LENGTH) continue;
          const sx = rec.x1 + other.x * rec.w1;
          const sy = rec.y1 - other.y * unitPx;
          other.draw(ctx, sx, sy, unitPx);
        }
      }
      ctx.globalAlpha = 1;
    }
    ctx.globalAlpha = 1;

    return {
      horizon,
      sway,
      riderX: riderInfo.x,
      riderY: riderInfo.y,
      riderRoadPx: riderInfo.w,
      riderUnitPx: riderInfo.w / ROAD_HALF_WIDTH,
    };
  }

  private drawSegment(ctx: CanvasRenderingContext2D, r: ProjectedSegment, theme: Theme, w: number): void {
    const dark = Math.floor(r.seg.index / RUMBLE_LENGTH) % 2 === 0;
    const fog = r.fog;

    const rawGround = dark ? theme.ground.dark : theme.ground.light;
    const ground = fogged(rawGround, theme.fog, fog);
    // Dirt does not stripe. Alternating the surface banded the trail like a
    // ladder, so the speed cue lives entirely in the rumble edges instead.
    const surface =
      theme.laneStyle === 'rut'
        ? theme.road.light
        : dark
          ? theme.road.dark
          : theme.road.light;
    const road = fogged(surface, theme.fog, fog);
    const rumble = fogged(dark ? theme.rumble.dark : theme.rumble.light, theme.fog, fog);

    const u1 = r.w1 / ROAD_HALF_WIDTH;
    const u2 = r.w2 / ROAD_HALF_WIDTH;
    const far = w * 2;
    const lb1 = r.y1 - r.seg.bankL * u1;
    const lb2 = r.y2 - r.seg.bankL * u2;
    const rb1 = r.y1 - r.seg.bankR * u1;
    const rb2 = r.y2 - r.seg.bankR * u2;

    // Two convex quads, one per shoulder. A single polygon around both sides
    // self-intersects whenever the banks differ in sign — a bench cut — and the
    // nonzero fill rule then punches holes straight through the hillside.
    // Ground that falls away blends toward the haze so it reads as air and
    // distance rather than as a black hole beside the trail.
    // Shade or haze first, then fog once. Fogging a colour and then shading it
    // leaves distant hillsides sitting black against a hazed world.
    const shoulder = (bank: number, lit: number): string =>
      bank > 0
        ? fogged(shade(rawGround, lit), theme.fog, fog)
        : fogged(mixHex(rawGround, theme.sky.mid, 0.5), theme.fog, fog);
    const leftLit = shoulder(r.seg.bankL, 0.06);
    const rightLit = shoulder(r.seg.bankR, 0.16);
    polygon(ctx, -far, lb1, r.x1 - r.w1, r.y1, r.x2 - r.w2, r.y2, -far, lb2, leftLit);
    polygon(ctx, r.x1 + r.w1, r.y1, far, rb1, far, rb2, r.x2 + r.w2, r.y2, rightLit);

    const edge = theme.laneStyle === 'dash' ? 0.11 : 0.07;
    const rw1 = r.w1 * edge;
    const rw2 = r.w2 * edge;
    polygon(ctx, r.x1 - r.w1 - rw1, r.y1, r.x1 - r.w1, r.y1, r.x2 - r.w2, r.y2, r.x2 - r.w2 - rw2, r.y2, rumble);
    polygon(ctx, r.x1 + r.w1 + rw1, r.y1, r.x1 + r.w1, r.y1, r.x2 + r.w2, r.y2, r.x2 + r.w2 + rw2, r.y2, rumble);
    polygon(ctx, r.x1 - r.w1, r.y1, r.x1 + r.w1, r.y1, r.x2 + r.w2, r.y2, r.x2 - r.w2, r.y2, road);

    if (r.seg.grip < 1) {
      // Ice or grease: a glassy sheen so low grip is visible before you feel it.
      polygon(
        ctx,
        r.x1 - r.w1,
        r.y1,
        r.x1 + r.w1,
        r.y1,
        r.x2 + r.w2,
        r.y2,
        r.x2 - r.w2,
        r.y2,
        rgba('#bff2ff', 0.28 * (1 - fog)),
      );
    }

    if (r.seg.stairs) {
      // One bright nosing per segment: at speed they march toward you as steps.
      const t = Math.max(1, (r.y1 - r.y2) * 0.42);
      ctx.fillStyle = fogged(shade(theme.road.light, 0.45), theme.fog, fog);
      ctx.fillRect(r.x1 - r.w1, r.y1 - t, r.w1 * 2, t);
    }

    this.drawLaneGuides(ctx, r, theme, fog, dark);

    if (r.seg.finish) {
      polygon(ctx, r.x1 - r.w1, r.y1, r.x1 + r.w1, r.y1, r.x2 + r.w2, r.y2, r.x2 - r.w2, r.y2, '#f5f5f5');
    }
  }

  private drawLaneGuides(
    ctx: CanvasRenderingContext2D,
    r: ProjectedSegment,
    theme: Theme,
    fog: number,
    dark: boolean,
  ): void {
    const style = theme.laneStyle;
    if (style === 'dash' && !dark) return;
    const width = style === 'dash' ? 0.018 : style === 'glow' ? 0.012 : 0.022;
    const colour =
      style === 'glow'
        ? rgba(theme.laneMark, 0.55 * (1 - fog))
        : style === 'rut'
          ? rgba('#000000', 0.1 * (1 - fog))
          : fogged(theme.laneMark, theme.fog, fog);
    const l1 = r.w1 * width;
    const l2 = r.w2 * width;
    for (const at of [-0.31, 0.31]) {
      const c1 = r.x1 + at * r.w1;
      const c2 = r.x2 + at * r.w2;
      polygon(ctx, c1 - l1, r.y1, c1 + l1, r.y1, c2 + l2, r.y2, c2 - l2, r.y2, colour);
    }
  }
}

interface Projected {
  x: number;
  y: number;
  w: number;
  scale: number;
  z: number;
}

export function project(
  worldZ: number,
  worldY: number,
  camX: number,
  camY: number,
  camZ: number,
  depth: number,
  width: number,
  height: number,
  pitch = 0,
): Projected {
  const dz = worldZ - camZ;
  const scale = depth / Math.max(1e-6, dz);
  return {
    x: Math.round(width / 2 + (scale * (0 - camX) * width) / 2),
    y: Math.round(height / 2 - (scale * (worldY - camY) * height) / 2 - pitch),
    w: Math.round((scale * ROAD_HALF_WIDTH * width) / 2),
    scale,
    z: dz,
  };
}

/**
 * The rider sits on the road at his own distance, interpolated between the two
 * ends of his segment. Positioning him against the camera's road centre instead
 * is what used to slide him off the trail through a corner.
 */
function placeRider(
  o: WorldRenderOptions,
  rec: ProjectedSegment | null,
  w: number,
  h: number,
): { x: number; y: number; w: number } {
  if (!rec || rec.w1 <= 0) {
    return { x: w / 2, y: h * 0.8, w: Math.max(1, w * 0.45) };
  }
  const t = clamp01((o.playerZ - rec.seg.z) / SEGMENT_LENGTH);
  const centre = rec.x1 + (rec.x2 - rec.x1) * t;
  const half = rec.w1 + (rec.w2 - rec.w1) * t;
  const baseY = rec.y1 + (rec.y2 - rec.y1) * t;
  const unitPx = half / ROAD_HALF_WIDTH;
  return {
    x: centre + (o.playerX - o.cameraX) * half,
    y: baseY - o.playerY * unitPx,
    w: half,
  };
}

/**
 * Screen y of the shoulder at a given screen x, so roadside props stand on the
 * hillside instead of floating at trail level.
 */
function shoulderY(r: ProjectedSegment, screenX: number, w: number): number {
  const far = w * 2;
  const right = screenX > r.x1;
  const edgeX = r.x1 + (right ? r.w1 : -r.w1);
  const bank = right ? r.seg.bankR : r.seg.bankL;
  const outerY = r.y1 - bank * (r.w1 / ROAD_HALF_WIDTH);
  const span = (right ? far : -far) - edgeX;
  if (Math.abs(span) < 1) return r.y1;
  const t = clamp01((screenX - edgeX) / span);
  return r.y1 + (outerY - r.y1) * t;
}

function polygon(
  ctx: CanvasRenderingContext2D,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  x3: number,
  y3: number,
  x4: number,
  y4: number,
  fill: string,
): void {
  ctx.fillStyle = fill;
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.lineTo(x3, y3);
  ctx.lineTo(x4, y4);
  ctx.closePath();
  ctx.fill();
}

const fogCache = new Map<string, string>();
function fogged(color: string, fog: string, amount: number): string {
  if (amount < 0.01) return color;
  const step = Math.round(amount * 48);
  const key = `${color}|${fog}|${step}`;
  let hit = fogCache.get(key);
  if (!hit) {
    hit = mixHex(color, fog, step / 48);
    fogCache.set(key, hit);
  }
  return hit;
}

/** Screen x of a lane centre at the rider's depth; used by the HUD. */
export function laneScreenX(frame: WorldFrame, lane: number, cameraX: number): number {
  return frame.riderX + ((LANE_X[lane] ?? 0) - cameraX) * frame.riderRoadPx;
}

export { shade };
